/**
 * 생기부 일괄 생성 — 스캔 PDF 학생 범위 확인 (감사 P0-2 / 권고 5).
 *
 * 서버가 스캔 PDF를 "학생별 페이지 범위"로 나눈 뒤 학생별 원문을 추출한다. 경계 감지가 실패하면
 * 1페이지=1명으로 강제 분할(even_split)해 그대로 넘어왔고, 교사가 확인할 기회가 없었다.
 * 여기서는 file_meta.analysis_meta 를 읽어 "범위 확인이 필요한가"를 판정하고,
 * 카드에 표시할 쪽 범위 문구를 만든다. 잘못 나뉜 학생은 교사가 원문을 고치거나 `제외`로 정리한다.
 */

export const SCAN_RANGE_MODE = 'subject_pdf_2stage';

const toInt = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * @param {object} fileMeta analyze-excel 응답의 file_meta (analysis_meta 포함)
 * @returns {{
 *   isScan: boolean, needsConfirm: boolean, confidence: string, rangeSource: string,
 *   pageCount: number, detectedRanges: number, evenSplitPagesPerStudent: number, reasons: string[]
 * }}
 */
export const getScanRangeReview = (fileMeta) => {
  const meta = fileMeta?.analysis_meta || {};
  const isScan = meta.mode === SCAN_RANGE_MODE;
  const confidence = String(meta.confidence || '').toLowerCase();
  const rangeSource = String(meta.range_source || (isScan ? 'model' : '')).toLowerCase();
  const pageCount = toInt(meta.page_count);
  const detectedRanges = toInt(meta.detected_ranges);
  const evenSplitPagesPerStudent = toInt(meta.even_split_pages_per_student);

  const reasons = [];
  if (isScan) {
    if (rangeSource === 'even_split') {
      reasons.push(
        evenSplitPagesPerStudent > 1
          ? `학생 경계를 찾지 못해 ${evenSplitPagesPerStudent}쪽씩 한 명으로 나눴어요.`
          : '학생 경계를 찾지 못해 1쪽을 한 명으로 나눴어요.'
      );
    } else if (confidence && confidence !== 'high') {
      reasons.push('학생 경계를 확신하지 못했어요.');
    }
  }

  return {
    isScan,
    needsConfirm: isScan && reasons.length > 0,
    confidence,
    rangeSource,
    pageCount,
    detectedRanges,
    evenSplitPagesPerStudent,
    reasons,
  };
};

/**
 * 나눠 읽다 빠진 쪽, 쪽수 한도로 잘린 쪽, 글자 수 한도로 잘린 내용을 교사 안내 문구로 모은다.
 * @param {object} fileMeta analyze-excel 응답의 file_meta
 * @param {boolean} wasTruncated 글자 수 한도로 잘렸는지
 * @returns {string[]}
 */
const readGapWarnings = (meta) => {
  const warnings = meta?.analysis_meta?.read_gaps?.warnings;
  return Array.isArray(warnings) ? warnings.filter((w) => typeof w === 'string' && w.trim()) : [];
};

export const getReadGapMessages = (fileMeta, wasTruncated = false) => {
  const messages = readGapWarnings(fileMeta);
  (Array.isArray(fileMeta?.files) ? fileMeta.files : []).forEach((file) => {
    readGapWarnings(file).forEach((w) => messages.push(file?.filename ? `${file.filename}: ${w}` : w));
  });
  (Array.isArray(fileMeta?.errors) ? fileMeta.errors : []).forEach((file) => {
    if (file?.filename) messages.push(`${file.filename}은(는) 읽지 못해 학생 목록에서 빠졌어요.`);
  });
  if (wasTruncated) {
    messages.push('파일 내용이 길어 뒷부분은 읽지 못했어요. 끝쪽 학생이 빠졌는지 확인해 주세요.');
  }
  return messages;
};

const uniqSortedPages = (pages) => (
  Array.from(new Set(Array.isArray(pages) ? pages : []))
    .map(toInt)
    .filter((p) => p > 0)
    .sort((x, y) => x - y)
);

/** 페이지 배열을 "3쪽" / "3-4쪽" / "3, 5쪽" 으로 */
export const formatSourcePages = (pages) => {
  const list = uniqSortedPages(pages);
  if (list.length === 0) return '';
  if (list.length === 1) return `${list[0]}쪽`;
  const contiguous = list.every((p, i) => i === 0 || p === list[i - 1] + 1);
  return contiguous ? `${list[0]}-${list[list.length - 1]}쪽` : `${list.join(', ')}쪽`;
};
